import React from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon } from '@/core/constants/webComponents';
import './EditLanguagesModal.scss';

interface LanguagesState {
  pt: boolean;
  en: boolean;
  es: boolean;
}

interface EditLanguagesModalProps {
  isOpen: boolean;
  selectedLanguages: LanguagesState;
  onChange: (nextLanguages: LanguagesState) => void;
  onFinish?: () => void;
  onClose: () => void;
}

const buildLanguageCardClassName = (isSelected: boolean, isFixed = false): string => {
  const states = ['new-setup__languages-modal-card'];

  if (isSelected) {
    states.push('new-setup__languages-modal-card--selected');
  } else {
    states.push('new-setup__languages-modal-card--unselected');
  }

  if (isFixed) {
    states.push('new-setup__languages-modal-card--fixed');
  }

  return states.join(' ');
};

const buildCheckboxClassName = (isSelected: boolean, isFixed = false): string => {
  const states = ['new-setup__languages-modal-checkbox'];

  if (isSelected) {
    states.push('new-setup__languages-modal-checkbox--selected');
  } else {
    states.push('new-setup__languages-modal-checkbox--unselected');
  }

  if (isFixed) {
    states.push('new-setup__languages-modal-checkbox--fixed');
  }

  return states.join(' ');
};

export const EditLanguagesModal: React.FC<EditLanguagesModalProps> = ({
  isOpen,
  selectedLanguages,
  onChange,
  onFinish,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!isOpen) {
    return null;
  }

  const toggleLanguage = (language: 'en' | 'es') => {
    onChange({
      ...selectedLanguages,
      pt: true,
      [language]: !selectedLanguages[language],
    });
  };

  const handleFinish = () => {
    onFinish?.();
    onClose();
  };

  return (
    <div className="new-setup__modal-overlay">
      <dialog open className="new-setup__languages-modal" aria-labelledby="new-setup-languages-modal-title">
        <header className="new-setup__languages-modal-header">
          <button
            type="button"
            className="new-setup__languages-modal-close"
            onClick={onClose}
            aria-label={t('newSetup.languagesModal.close')}
          >
            <BdsIcon theme="outline" name="close" size="medium" aria-hidden="true" />
          </button>

          <h2 id="new-setup-languages-modal-title" className="new-setup__languages-modal-title">
            {t('newSetup.languagesModal.title')}
          </h2>
          <p className="new-setup__languages-modal-subtitle">{t('newSetup.languagesModal.subtitle')}</p>
        </header>

        <div className="new-setup__languages-modal-content">
          <div className="new-setup__languages-modal-divider" />

          <div className="new-setup__languages-modal-copy">
            <h3 className="new-setup__languages-modal-copy-title">{t('newSetup.languagesModal.question')}</h3>
            <p className="new-setup__languages-modal-copy-text">{t('newSetup.languagesModal.description')}</p>
          </div>

          <div className="new-setup__languages-modal-cards">
            <div className={buildLanguageCardClassName(true, true)}>
              <span className="new-setup__languages-modal-checkbox-wrap new-setup__languages-modal-checkbox-wrap--fixed" aria-hidden="true">
                <span className={buildCheckboxClassName(true, true)}>
                  <BdsIcon name="check" size="x-small" color="#282828" aria-hidden="true" />
                </span>
              </span>
              <div className="new-setup__languages-modal-card-labels">
                <p className="new-setup__languages-modal-card-title">{t('newSetup.languagesModal.portuguese')}</p>
                <p className="new-setup__languages-modal-card-code">PT</p>
              </div>
            </div>

            <button
              type="button"
              className={buildLanguageCardClassName(selectedLanguages.en)}
              onClick={() => toggleLanguage('en')}
              aria-pressed={selectedLanguages.en}
              aria-label={t('newSetup.languagesModal.toggleEnglish')}
            >
              <span className="new-setup__languages-modal-checkbox-wrap" aria-hidden="true">
                <span className={buildCheckboxClassName(selectedLanguages.en)}>
                  {selectedLanguages.en && <BdsIcon name="check" size="x-small" color="#FFFFFF" aria-hidden="true" />}
                </span>
              </span>
              <div className="new-setup__languages-modal-card-labels">
                <p className="new-setup__languages-modal-card-title">{t('newSetup.languagesModal.english')}</p>
                <p className="new-setup__languages-modal-card-code">EN</p>
              </div>
            </button>

            <button
              type="button"
              className={buildLanguageCardClassName(selectedLanguages.es)}
              onClick={() => toggleLanguage('es')}
              aria-pressed={selectedLanguages.es}
              aria-label={t('newSetup.languagesModal.toggleSpanish')}
            >
              <span className="new-setup__languages-modal-checkbox-wrap" aria-hidden="true">
                <span className={buildCheckboxClassName(selectedLanguages.es)}>
                  {selectedLanguages.es && <BdsIcon name="check" size="x-small" color="#FFFFFF" aria-hidden="true" />}
                </span>
              </span>
              <div className="new-setup__languages-modal-card-labels">
                <p className="new-setup__languages-modal-card-title">{t('newSetup.languagesModal.spanish')}</p>
                <p className="new-setup__languages-modal-card-code">ES</p>
              </div>
            </button>
          </div>
        </div>

        <footer className="new-setup__languages-modal-footer">
          <button type="button" className="new-setup__languages-modal-confirm" onClick={handleFinish}>
            {t('newSetup.languagesModal.finish')}
          </button>
        </footer>
      </dialog>
    </div>
  );
};
